/* eslint-disable react/style-prop-object */
/* eslint-disable max-len */
import { darken, useTheme } from '@material-ui/core';
import React, { CSSProperties, useMemo } from 'react';


export interface WarningIconProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly background?: string | undefined;
    readonly foreground?: string | undefined;
}


/**
 * This is a React component what wrap the warning's SVG image. This allow us to use it in other React components and customize it.
 */
export const WarningIcon: React.FC<WarningIconProps> = (props: WarningIconProps) => {

    const theme = useTheme();

    // Give the warning image an instanceId so that we append the instanceId to the SVG elements' IDs to make them unique. These are HTML Element ID
    // so if we don't give them unique IDs, we can't create multiple instances of this WarningIcon component.
    const instanceId = useMemo(() => {
        return Math.random() * 1000 * Date.now();
    }, []);


    const backgroundColor = useMemo((): {
        readonly startColor: string;
        readonly stopColor: string;
    } => {
        // Get the color from props, use color "#ff0a31" as default if color is not provided
        const color = props.background ?? theme.palette.warning.main;
        return {
            startColor: color,
            stopColor : darken(color, 0.1),
        };
    }, [props.background, theme.palette.warning.main]);


    const foregroundColor = useMemo((): string => {
        return props.foreground ?? theme.palette.warning.contrastText;
    }, [props.foreground, theme.palette.warning.contrastText]);


    return (
        <svg
            className={props.className}
            style={props.style}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 148.4 137.64"
        >
            <defs>
                <linearGradient id={`background_${instanceId}`} y2="115.22" gradientUnits="userSpaceOnUse" x2="120" gradientTransform="translate(.85 3.327)" y1="57.222" x1="58">
                    <stop
                        style={{
                            stopColor: backgroundColor.startColor,
                        }}
                        offset="0"
                    />
                    <stop
                        style={{
                            stopColor: backgroundColor.stopColor,
                        }}
                        offset="1"
                    />
                </linearGradient>
                <filter id={`blur_${instanceId}`} colorInterpolationFilters="sRGB">
                    <feGaussianBlur stdDeviation="2" />
                </filter>
            </defs>
            <path
                style={{
                    filter     : `url(#blur_${instanceId})`,
                    stroke     : '#000',
                    strokeWidth: 1,
                    fill       : 'none',
                }}
                d="M74.202 4.707a5.43 5.43 0 0 0-2.268.492c-.695.32-1.32.776-1.845 1.347-.525.57-.844 1.323-1.24 2.003C46.546 46.852 26.246 86.49 4.92 125.44c-.213.133-.213.765-.213 1.183 0 .87.16 1.709.454 2.463a6.357 6.357 0 0 0 1.24 2.004 5.797 5.797 0 0 0 1.845 1.346 5.43 5.43 0 0 0 2.268.493h127.379c.802 0 1.573-.174 2.268-.493a5.797 5.797 0 0 0 1.845-1.346 6.355 6.355 0 0 0 1.24-2.004 6.802 6.802 0 0 0 .453-2.463c0-.418 0-1.05-.211-1.183-21.32-38.942-41.62-78.578-63.925-116.88-.396-.68-.715-1.433-1.24-2.003a5.797 5.797 0 0 0-1.845-1.347 5.43 5.43 0 0 0-2.268-.492z"
            />
            <path
                style={{
                    stroke     : '#fff',
                    strokeWidth: 8,
                    fill       : '#fff',
                }}
                d="M71.845 4.827a5.43 5.43 0 0 0-2.268.493c-.695.319-1.32.776-1.845 1.346-.525.57-.844 1.324-1.24 2.004C44.19 46.973 23.89 86.609 2.562 125.56c-.213.132-.213.765-.213 1.182 0 .87.16 1.71.454 2.464a6.357 6.357 0 0 0 1.24 2.003 5.797 5.797 0 0 0 1.845 1.347 5.43 5.43 0 0 0 2.268.492h127.379c.802 0 1.573-.173 2.268-.492a5.797 5.797 0 0 0 1.845-1.347 6.355 6.355 0 0 0 1.24-2.003 6.802 6.802 0 0 0 .453-2.464c0-.417 0-1.05-.211-1.182C119.8 86.609 99.5 46.973 77.198 8.67c-.396-.68-.715-1.433-1.24-2.004a5.797 5.797 0 0 0-1.845-1.346 5.43 5.43 0 0 0-2.268-.493z"
            />
            <path
                style={{
                    fill: `url(#background_${instanceId})`,
                }}
                d="M71.745 13.604c-.716 0-1.405.155-2.025.44a5.176 5.176 0 0 0-1.647 1.202c-.47.51-.754 1.182-1.107 1.789-19.913 34.199-37.411 67.43-57.081 104.37-.168.316-.19.683-.19 1.056 0 .777.143 1.525.406 2.2.262.673.638 1.278 1.107 1.788s1.027.917 1.647 1.202c.62.285 1.31.44 2.025.44h113.73c.716 0 1.405-.155 2.025-.44a5.176 5.176 0 0 0 1.647-1.202c.47-.51.845-1.115 1.107-1.789a6.052 6.052 0 0 0 .405-2.2c0-.372-.02-.74-.189-1.055-18.66-34.951-37.177-70.163-57.085-104.36-.353-.607-.638-1.28-1.107-1.789a5.174 5.174 0 0 0-1.647-1.202 4.848 4.848 0 0 0-2.025-.44z"
            />
            <path
                d="M77 43.063a9 9 0 0 0-9 9c0 .118-.004.227 0 .344l4 48h.031c.182 2.596 2.326 4.656 4.969 4.656s4.787-2.06 4.969-4.656H82l3.781-45.344a18.18 18.18 0 0 0 .22-2.66c.004-.116 0-.225 0-.343a9 9 0 0 0-9-9z"
                style={{
                    fill: `${foregroundColor}`,
                }}
                transform="translate(-6.484 -2.742)"
            />
            <path
                style={{
                    fill: `${foregroundColor}`,
                }}
                d="M22 57.407c0 4.97-4.925 9-11 9s-11-4.03-11-9 4.925-9 11-9 11 4.03 11 9z"
                transform="matrix(.81818 0 0 1 61.516 56.258)"
            />
        </svg>
    );
};
